import { careersPageUrl } from '@/modules/companies/careersPageUrl';
import { useCompanyInboundSettingsQuery } from '@/modules/companies/queries/useCompanyInboundSettingsQuery';
import { useCompanyQuery } from '@/modules/companies/queries/useCompanyQuery';
import { useJobInboundSettingsQuery } from '@/modules/jobs/queries/useJobInboundSettingsQuery';
import type { JobApi } from '@/modules/jobs/types';

export type CareersPage =
  /** The company's Careers Page is on; `isOpen` is unknown until the job's settings load. */
  | { isEnabled: true; url: string; isOpen: boolean | undefined }
  /** It is off: the hiring manager can turn it on in the Workspace. */
  | { isEnabled: false };

/**
 * The company's Careers Page as the job page shows it, or undefined while
 * unknown or not the user's to manage. Inbound is for Self-Service Tenants,
 * as in the job editor (useEditorInbound); the API's inbound settings refuse
 * managed hiring managers.
 */
export const useCareersPage = (
  job: JobApi | undefined,
): CareersPage | undefined => {
  const companyId = job?.companyId ?? '';
  const companyQuery = useCompanyQuery(companyId, {
    enabled: Boolean(companyId),
  });
  const canManage = companyQuery.data?.isSelfServiceWorkspace === true;
  const companySettings = useCompanyInboundSettingsQuery(
    canManage ? companyId : undefined,
  ).data;
  const isEnabled = companySettings?.isCareersPageEnabled === true;
  const jobSettings = useJobInboundSettingsQuery(
    isEnabled ? job?.id : undefined,
  ).data;

  if (!companySettings) return undefined;
  if (!isEnabled || !companySettings.careersPageSlug) {
    return { isEnabled: false };
  }
  return {
    isEnabled: true,
    url: careersPageUrl(
      window.location.origin,
      companySettings.careersPageSlug,
    ),
    isOpen: jobSettings?.isOpenForApplications,
  };
};
